import React, { useCallback, useEffect, useMemo } from 'react'
import { ILocationSearchItemFragment } from 'transfix-maps/hooks/useAutocompleteLocation'
import { Button } from 'transfix-ui/components/Button'
import { Pagination } from 'transfix-ui/components/Pagination'
import PickupDropoffSelect, {
  IPickupDropoffSelectProps,
  IPickupDropoffSelectValue,
} from 'transfix-ui/components/PickupDropoffSelect'
import { Spinner } from 'transfix-ui/components/Spinner'
import { Tag, TAG_SIZE, TAG_STYLE } from 'transfix-ui/components/Tag'
import { Typography } from 'transfix-ui/core'
import { NumberParam, StringParam } from 'use-query-params'
import { DedicatedLanesOfferCard } from 'apps/fleet-planner/components/dedicated-lanes/DedicatedLanesOfferCard'
import { EquipmentFilterSingleSelect } from 'apps/fleet-planner/components/dedicated-lanes/EquipmentFilterSingleSelect'
import { ReactComponent as NoLoadsIllustration } from 'apps/fleet-planner/components/illustrations/NoLoadsIllustration.svg'
import { useGetDedicatedLanes } from 'apps/marketplace/apiTypes/dedicatedLanes'
import { useQueryParams } from 'utils/hooks'
import { FilterLoadingMethod } from '../loads/FilterLoadingMethod'
import { DEFAULT_RADIUS } from './constants'
import { DedicatedLanesQueryParams } from './types'
import { useFindOpportunitiesStyles } from './useFindOpportunitiesStyles'
import { getDedicatedLanesLocationFilterValue, scrollToLastPosition } from './utils'

export const DedicatedLanesFindOpportunitiesSubpage = () => {
  const classes = useFindOpportunitiesStyles()

  const [queryParams, setQueryParams] = useQueryParams<DedicatedLanesQueryParams>({
    page: NumberParam,
    pickupCity: StringParam,
    pickupStateCode: StringParam,
    pickupMiles: NumberParam,
    deliveryCity: StringParam,
    deliveryStateCode: StringParam,
    deliveryMiles: NumberParam,
    pickupLoadMethod: StringParam,
    deliveryLoadMethod: StringParam,
    trailerType: StringParam,
  })
  const {
    deliveryCity,
    deliveryLoadMethod,
    deliveryMiles,
    deliveryStateCode,
    page: currentPage = 1,
    pickupCity,
    pickupLoadMethod,
    pickupMiles,
    pickupStateCode,
    trailerType,
  } = queryParams || {}

  const { data, loading } = useGetDedicatedLanes({
    base: __API__,
    queryParams: {
      filters: {
        pickupCity,
        pickupStateCode,
        pickupMiles,
        deliveryCity,
        deliveryStateCode,
        deliveryMiles,
        pickupLoadMethod,
        deliveryLoadMethod,
        trailerType,
      },
      perPage: 20,
      page: currentPage,
    },
  })
  const { dedicatedLanes = [], pageCount = 0, totalCount = 0 } = data || {}

  /**
   * When data is finished loading, restore scroll position
   */
  useEffect(() => {
    if (!loading && data) {
      scrollToLastPosition()
    }
  }, [data, loading])

  const pickupDropoffValue: IPickupDropoffSelectValue = useMemo(
    () => ({
      pickup: getDedicatedLanesLocationFilterValue({
        city: pickupCity,
        stateCode: pickupStateCode,
      }),
      pickupRadius: pickupMiles,
      dropoff: getDedicatedLanesLocationFilterValue({
        city: deliveryCity,
        stateCode: deliveryStateCode,
      }),
      dropoffRadius: deliveryMiles,
    }),
    [deliveryCity, deliveryMiles, deliveryStateCode, pickupCity, pickupMiles, pickupStateCode]
  )

  const loadingMethodValue = useMemo(() => {
    if (!pickupLoadMethod || !deliveryLoadMethod) {
      return undefined
    }
    return `${pickupLoadMethod}-${deliveryLoadMethod}`
  }, [deliveryLoadMethod, pickupLoadMethod])

  const equipmentValue = useMemo(() => {
    return trailerType || undefined
  }, [trailerType])

  const pickupDropoffChangeHandler: IPickupDropoffSelectProps['onChange'] = (key, value) => {
    const paramKey = key.includes('pickup') ? 'pickup' : 'delivery'

    // If location is changing
    if (key === 'pickup' || key === 'dropoff') {
      if (!value) {
        setQueryParams({
          [`${paramKey}City`]: undefined,
          [`${paramKey}StateCode`]: undefined,
          [`${paramKey}Miles`]: undefined,
          page: undefined,
        })
        return
      }
      const { data } = value as { data: ILocationSearchItemFragment }
      const { city, stateCode } = data || {}
      setQueryParams({
        [`${paramKey}City`]: city,
        [`${paramKey}StateCode`]: stateCode,
        [`${paramKey}Miles`]: DEFAULT_RADIUS,
        page: undefined,
      })
      return
    }

    // If radius is changing
    if (key === 'pickupRadius' || key === 'dropoffRadius') {
      setQueryParams({
        [`${paramKey}Miles`]: value || DEFAULT_RADIUS,
        page: undefined,
      })
    }
  }

  const pickupDropoffSwapHandler: IPickupDropoffSelectProps['onSwap'] = () => {
    setQueryParams({
      pickupCity: deliveryCity,
      pickupStateCode: deliveryStateCode,
      pickupMiles: deliveryMiles,
      deliveryCity: pickupCity,
      deliveryStateCode: pickupStateCode,
      deliveryMiles: pickupMiles,
      page: undefined,
    })
  }

  const loadingMethodChangeHandler = (event: string | undefined) => {
    const loadingMethods = event ? event.split('-') : ''
    setQueryParams({
      page: undefined,
      pickupLoadMethod: loadingMethods[0],
      deliveryLoadMethod: loadingMethods[1],
    })
  }

  const equipmentChangeHandler = (event: string | undefined) => {
    setQueryParams({
      page: undefined,
      trailerType: event,
    })
  }

  const clearHandler = () => {
    setQueryParams({
      page: undefined,
      pickupCity: undefined,
      pickupStateCode: undefined,
      pickupMiles: undefined,
      deliveryCity: undefined,
      deliveryStateCode: undefined,
      deliveryMiles: undefined,
      pickupLoadMethod: undefined,
      deliveryLoadMethod: undefined,
      trailerType: undefined,
    })
  }

  const pageChangeHandler = useCallback(
    (pageNumber: number) => {
      setQueryParams({
        page: pageNumber,
      })
    },
    [setQueryParams]
  )

  const empty = !loading && !data?.dedicatedLanes?.length
  return (
    <div className={classes.main}>
      <div className={classes.searchBar}>
        <div>
          <PickupDropoffSelect
            value={pickupDropoffValue}
            onChange={pickupDropoffChangeHandler}
            onSwap={pickupDropoffSwapHandler}
            enableAnyPickup
            enableAnyDropoff
          />
        </div>
        <div className={classes.secondaryFiltersRow}>
          <div className={classes.pillFilters}>
            <FilterLoadingMethod value={loadingMethodValue} onChange={loadingMethodChangeHandler} />
            <EquipmentFilterSingleSelect value={equipmentValue} onChange={equipmentChangeHandler} />
          </div>
          <Button variant='text' onClick={clearHandler} className={classes.clearButton}>
            Clear Filters
          </Button>
        </div>
      </div>
      <div>
        <div className={classes.resultsHeader}>
          <Typography variant='subtitle1'>Results</Typography>
          {loading && <Tag label='Loading' size={TAG_SIZE.xsmall} tagStyle={TAG_STYLE.dark} />}
          {!loading && (
            <Tag label={totalCount.toString()} size={TAG_SIZE.xsmall} tagStyle={TAG_STYLE.dark} />
          )}
        </div>
      </div>
      <div className={classes.results}>
        {loading ? (
          <Spinner />
        ) : (
          !empty && (
            <div>
              {dedicatedLanes?.map(lane => (
                <DedicatedLanesOfferCard key={lane.id} dedicatedLane={lane} />
              ))}
            </div>
          )
        )}
        {!loading && !empty && (
          <div className={classes.pagination}>
            <Pagination
              currentPage={currentPage}
              numberOfPages={pageCount}
              onPageChange={pageChangeHandler}
            />
          </div>
        )}
      </div>
      {!loading && empty && (
        <div className={classes.emptyState}>
          <NoLoadsIllustration />
          <Typography variant='subtitle1'>No opportunities found</Typography>
          <Typography variant='body2'>
            Try adjusting your search criteria to find dedicated opportunities
          </Typography>
        </div>
      )}
    </div>
  )
}
